export type AuthorizedFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  errorMessage?: string;
  headers?: Record<string, string>;
}

export interface ApiClient {
  get<T>(url: string, options?: ApiClientOptions): Promise<T>;
  post<T>(url: string, body?: unknown, options?: ApiClientOptions): Promise<T>;
  delete<T>(url: string, options?: ApiClientOptions): Promise<T | undefined>;
}

async function handleResponse<T>(res: Response, defaultError: string): Promise<T> {
  if (!res.ok) {
    let message = `${defaultError} (${res.status})`;
    try {
      const data: unknown = await res.json();
      if (data && typeof data === 'object') {
        const m = (data as { message?: unknown }).message;
        if (typeof m === 'string' && m.trim()) {
          message = m;
        }
      }
    } catch {
      // ignore JSON parse errors, fallback message already set
    }
    throw new Error(message);
  }

  // Handle 204 No Content - no body to parse
  if (res.status === 204) {
    return undefined as T;
  }

  // Attempt to parse JSON body; if parsing fails, surface a meaningful error
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error('Invalid server response format.');
  }
}

async function handleDeleteResponse(res: Response, defaultError: string): Promise<void> {
  if (!res.ok) {
    let message = `${defaultError} (${res.status})`;
    try {
      const data: unknown = await res.json();
      if (data && typeof data === 'object') {
        const m = (data as { message?: unknown }).message;
        if (typeof m === 'string' && m.trim()) {
          message = m;
        }
      }
    } catch {
      // ignore JSON parse errors, fallback message already set
    }
    throw new Error(message);
  }
  // No body expected on successful delete
}

export function createApiClient(authorizedFetch: AuthorizedFetch): ApiClient {
  return {
    async get<T>(url: string, options?: ApiClientOptions): Promise<T> {
      const res = await authorizedFetch(url, { method: 'GET', headers: options?.headers });
      return handleResponse<T>(res, options?.errorMessage ?? 'Request failed');
    },
    async post<T>(url: string, body?: unknown, options?: ApiClientOptions): Promise<T> {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options?.headers ?? {}),
      };
      const res = await authorizedFetch(url, {
        method: 'POST',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      return handleResponse<T>(res, options?.errorMessage ?? 'Request failed');
    },
    async delete<T>(url: string, options?: ApiClientOptions): Promise<T | undefined> {
      const res = await authorizedFetch(url, { method: 'DELETE', headers: options?.headers });
      await handleDeleteResponse(res, options?.errorMessage ?? 'Request failed');
      return undefined as T;
    },
  };
}
